import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from './ui/Card';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from './ui/Alert';
import { Badge } from './ui/Badge';
import { AlertTriangle, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';

interface RiskProfile {
  riskLevel: 'normal' | 'elevated' | 'high' | 'restricted';
  currentBalance: number;
  pendingBalance: number;
  totalDisputes: number;
  disputeRate: number;
  restrictions: string[];
}

interface RiskAssessment {
  riskLevel: 'normal' | 'elevated' | 'high' | 'restricted';
  reasons: string[];
  recommendations: string[];
}

interface RemediationRequest {
  id: string;
  type: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'escalated';
  requirements?: any;
  dueDate?: string;
}

interface RiskStatusCardProps {
  className?: string;
  /** Risk profiles are academy-scoped; skip fetch when the operator has no academy (e.g. club-only). */
  enabled?: boolean;
}

export const RiskStatusCard: React.FC<RiskStatusCardProps> = ({ className, enabled = true }) => {
  const [profile, setProfile] = useState<RiskProfile | null>(null);
  const [assessment, setAssessment] = useState<RiskAssessment | null>(null);
  const [remediations, setRemediations] = useState<RemediationRequest[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setProfile(null);
      setAssessment(null);
      setRemediations([]);
      setError(null);
      return;
    }
    void loadRiskData();
  }, [enabled]);

  const loadRiskData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get auth token
      let token = '';
      if (auth.currentUser) {
        try {
          token = await auth.currentUser.getIdToken();
        } catch (error) {
          console.error('Error getting ID token:', error);
        }
      }

      const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      // Load risk profile and assessment
      const profileResponse = await api.get('/risk/profile');
      if (profileResponse.data.success) {
        setProfile(profileResponse.data.data.profile);
        setAssessment(profileResponse.data.data.assessment);
      }

      // Load active remediation requests
      const remediationResponse = await api.get('/risk/remediation?active=true');
      if (remediationResponse.data.success) {
        setRemediations(remediationResponse.data.data.requests);
      }
    } catch (err: any) {
      const status = err.response?.status as number | undefined;
      // Club-only operators and orgs without a risk profile row should not see an error card.
      if (status === 404) {
        setProfile(null);
        setAssessment(null);
        setRemediations([]);
        setError(null);
        return;
      }
      console.error('Error loading risk data:', err);
      setError(err.response?.data?.error || 'Failed to load risk status');
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevelColor = (level: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' => {
    switch (level) {
      case 'normal':
        return 'success';
      case 'elevated':
        return 'warning';
      case 'high':
        return 'destructive';
      case 'restricted':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getRiskLevelIcon = (level: string) => {
    switch (level) {
      case 'normal':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'elevated':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'restricted':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  // Reserve no layout space while loading, disabled, or when there is nothing to show.
  if (!enabled || loading) {
    return null;
  }

  if (error) {
    return (
      <Card className={cn(className)}>
        <CardHeader>
          <h3 className="text-lg font-semibold">Account Risk Status</h3>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!profile || !assessment) {
    return null;
  }

  // Only show the card if there are issues or active remediations
  if (profile.riskLevel === 'normal' && remediations.length === 0) {
    return null;
  }

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Account Risk Status</h3>
          <div className="flex items-center gap-2">
            {getRiskLevelIcon(profile.riskLevel)}
            <Badge variant={getRiskLevelColor(profile.riskLevel)}>
              {profile.riskLevel.toUpperCase()}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Show alert for elevated risk levels */}
        {profile.riskLevel !== 'normal' && (
          <Alert 
            variant={profile.riskLevel === 'restricted' || profile.riskLevel === 'high' ? 'destructive' : 'warning'}
            className="mb-4"
          >
            <AlertDescription>
              <strong>Action Required:</strong> Your account has been flagged for risk review.
              {assessment.reasons.length > 0 && (
                <ul className="mt-2 ml-4 list-disc">
                  {assessment.reasons.map((reason, index) => (
                    <li key={index}>{reason}</li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Show negative balance alert */}
        {profile.currentBalance < 0 && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              <strong>Negative Balance:</strong> Your account has a negative balance of{' '}
              ${Math.abs(profile.currentBalance / 100).toFixed(2)}. Please resolve this immediately.
            </AlertDescription>
          </Alert>
        )}

        {/* Show active remediation requests */}
        {remediations.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900">Required Actions:</h4>
            {remediations.map((remediation) => (
              <div
                key={remediation.id}
                className="p-3 border border-yellow-200 bg-yellow-50 rounded-lg"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {remediation.type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </p>
                    {remediation.dueDate && (
                      <p className="text-sm text-gray-600 mt-1">
                        Due: {new Date(remediation.dueDate).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Badge variant={remediation.status === 'pending' ? 'warning' : 'default'}>
                    {remediation.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Show recommendations */}
        {assessment.recommendations.length > 0 && (
          <div className="mt-4">
            <h4 className="font-semibold text-gray-900 mb-2">Recommendations:</h4>
            <ul className="space-y-1">
              {assessment.recommendations.map((rec, index) => (
                <li key={index} className="text-sm text-gray-600">
                  • {rec}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Account metrics */}
        {(profile.totalDisputes > 0 || profile.disputeRate > 0) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">Account Metrics:</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {profile.totalDisputes > 0 && (
                <div>
                  <p className="text-gray-600">Total Disputes</p>
                  <p className="font-semibold text-gray-900">{profile.totalDisputes}</p>
                </div>
              )}
              {profile.disputeRate > 0 && (
                <div>
                  <p className="text-gray-600">Dispute Rate</p>
                  <p className="font-semibold text-gray-900">
                    {(profile.disputeRate * 100).toFixed(2)}%
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
